import { parseMoneyToCents, sumCents, tt, type MoneyInput } from '@agos/shared'
import type { BadgeColor } from '@agos/ui'

/**
 * The receipts a register has printed.
 *
 * §14.2 / §11.4 is the whole reason this page needs care: a **virtual** receipt
 * has no UID because it never reached the tax authority's device, and it therefore
 * **cannot be storned on the device either**. The restored dumps hold 2 177 of
 * them. A screen that offers a storno button on one is offering something the
 * register cannot do.
 *
 * Ported from legacy `pages/fiskal-receipts.tsx`.
 */

export interface FiskalReceiptLine {
  productName?: string
  quantity?: number
  unitPrice?: MoneyInput
  totalPrice?: MoneyInput
  /**
   * The tax the line was registered under — §10A.3's snapshot, added to this interface 2026-08-05.
   *
   * The endpoint has answered both since §10A.3 and this interface did not declare them, so nothing
   * could read them without TypeScript refusing. Text rather than a number, unlike `GET /orders`:
   * this query reads the column through `LegacyRows.of`, which keeps a numeric as the text Postgres
   * sent.
   */
  vatRate?: MoneyInput | null
  vatLetter?: string | null
}

export interface FiskalReceipt {
  id?: number
  orderNumber?: string
  type?: string
  fiskalPrintedAt?: string
  fiskalKasaId?: string
  /** Null on a virtual receipt — the register never issued one. */
  fiskalReceiptNumber?: string | null
  fiskalVirtual?: boolean
  fiskalStornoAt?: string | null
  fiskalStornoBy?: string | null
  total?: MoneyInput
  /**
   * What the document was printed for — `BACKEND.md` §10A.23.
   *
   * Absent for every receipt printed before the column existed, which is all 6 568 in the restored
   * data. Present ones are read beside `total`, which is what the order says **now**.
   */
  printedTotal?: MoneyInput
  /**
   * How many of this receipt's lines arrived after it was written — `BACKEND.md` §10A.23.
   *
   * Unlike `printedTotal` this is computed rather than stored, so it answers for the **6 568
   * receipts printed before that column existed** — which are the ones the finding is about.
   */
  linesAddedAfterPrint?: number
  subtotal?: MoneyInput
  discount?: MoneyInput
  deliveryCost?: MoneyInput
  paymentMethod?: string
  vatRate?: MoneyInput
  vatBase?: MoneyInput
  vatAmount?: MoneyInput
  items?: FiskalReceiptLine[]
}

export type ReceiptState = 'valid' | 'virtual' | 'storned' | 'storned-virtual'

/**
 * What a receipt is.
 *
 * `virtual` and `valid` are both legitimate — §14.2 says a NULL UID is a valid
 * state, not an error — but they are not the same thing, and only one of them can
 * be storned on the device. `storned-virtual` exists because a virtual receipt
 * *can* be marked storned locally; the mark is bookkeeping, not a device
 * operation, and the screen has to be able to say which it was.
 */
/**
 * Whether the order has moved since its receipt was printed — §10A.23.
 *
 * **Null when the receipt predates the record**, which is not the same as agreement: 6 568 receipts
 * in the restored data have no printed total, and saying they match would be inventing the very
 * agreement the audit behind this was testing.
 *
 * The audit found **73 orders carrying 241 items inserted after their receipt printed**, worth
 * €1 810,10. This is what makes the next one visible on the screen rather than only in a query.
 */
export function printedTotalDrift(receipt: FiskalReceipt): number | null {
  if (receipt.printedTotal === undefined || receipt.printedTotal === null) return null
  return parseMoneyToCents(receipt.total) - parseMoneyToCents(receipt.printedTotal)
}

/**
 * Whether lines arrived after the document was written — §10A.23.
 *
 * **This is the one that works on the receipts the owner already has.** `printedTotal` is null for
 * all 6 568 of them; this is derived from each line's own `created_at`, so it answers for every
 * receipt ever printed. 73 of them are non-zero: 241 lines, €1 810,10.
 */
export function linesAddedAfterPrint(receipt: FiskalReceipt): number {
  return receipt.linesAddedAfterPrint ?? 0
}

/**
 * What to do about a receipt whose order has moved — §9.81, owner decision 2026-08-07.
 *
 * **Storno the original and reprint the whole bill.** Chosen against a second receipt for the extra
 * items and against reconciling by hand. Both mechanisms already exist: `POST /fiskal/storno`
 * re-authenticates and refuses a second reversal, and `print` permits a new receipt once the storno
 * is recorded — its guard is *printed and not storno'd*.
 *
 * Null when there is nothing to say, so the row stays quiet on the ordinary receipt. Guidance only:
 * the screen never reverses a tax document on somebody's behalf.
 */
export function remedyForDrift(receipt: FiskalReceipt): string | null {
  if (!driftsFromPrinted(receipt) && linesAddedAfterPrint(receipt) === 0) return null
  return tt('Objednávka sa zmenila po vytlačení - stornujte doklad a vytlačte nový.')
}

/** A receipt whose order has grown or shrunk since it printed — worth saying out loud. */
export function driftsFromPrinted(receipt: FiskalReceipt): boolean {
  const drift = printedTotalDrift(receipt)
  return drift !== null && drift !== 0
}

export function receiptState(receipt: FiskalReceipt): ReceiptState {
  const virtual = receipt.fiskalVirtual === true || !receipt.fiskalReceiptNumber
  const storned = Boolean(receipt.fiskalStornoAt)
  if (storned) return virtual ? 'storned-virtual' : 'storned'
  return virtual ? 'virtual' : 'valid'
}

export const STATE_BADGES: Record<ReceiptState, { label: string; color: BadgeColor }> = {
  valid: { label: tt('Platný'), color: 'emerald' },
  virtual: { label: tt('Virtuálny'), color: 'amber' },
  storned: { label: tt('Stornovaný'), color: 'red' },
  'storned-virtual': { label: tt('Stornovaný lokálne'), color: 'orange' },
}

/**
 * Whether the device can storno this receipt.
 *
 * A virtual receipt cannot: the register does not know it exists, so there is
 * nothing on the device to reverse. Legacy offers the button anyway and only
 * explains afterwards, in a toast, once the attempt has come back saying so.
 */
export function canStornoOnDevice(receipt: FiskalReceipt): boolean {
  return receiptState(receipt) === 'valid'
}

/** Why the button is unavailable, so the screen can say it before it is pressed. */
export function stornoBlockedReason(receipt: FiskalReceipt): string | null {
  const state = receiptState(receipt)
  if (state === 'valid') return null
  if (state === 'virtual') {
    return tt('Doklad bol vystavený vo virtuálnom režime - kasa o ňom nevie, takže ho nedokáže fiskálne stornovať.')
  }
  return tt('Doklad je už stornovaný.')
}

export interface ReceiptTotals {
  count: number
  /** In cents — §14.7. */
  grossCents: number
  vatCents: number
  /** Receipts the authority never saw. */
  virtualCount: number
  stornoCount: number
}

/**
 * The header figures.
 *
 * A storned receipt is counted but **excluded from the takings** — it was
 * reversed, so including it would report money the business does not have. Legacy
 * sums every row.
 */
export function receiptTotals(receipts: FiskalReceipt[]): ReceiptTotals {
  const counted = receipts.filter((receipt) => !receipt.fiskalStornoAt)

  return {
    count: receipts.length,
    grossCents: sumCents(...counted.map((receipt) => receipt.total)),
    vatCents: sumCents(...counted.map((receipt) => receipt.vatAmount)),
    virtualCount: receipts.filter((receipt) => {
      const state = receiptState(receipt)
      return state === 'virtual' || state === 'storned-virtual'
    }).length,
    stornoCount: receipts.filter((receipt) => Boolean(receipt.fiskalStornoAt)).length,
  }
}

/**
 * The register a receipt came from.
 *
 * §10A.1 made these franchise-managed channels; §9.5 replaces the map. A third
 * register shows its raw id rather than being mislabelled as one of the two.
 */
const KASA_LABELS: Record<string, string> = {
  rozvoz: tt('Rozvoz'),
  restauracia: tt('Reštaurácia'),
}

export function kasaLabel(kasaId: string | undefined): string {
  return KASA_LABELS[kasaId ?? ''] ?? kasaId ?? ''
}

export function matchesSearch(receipt: FiskalReceipt, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true

  return [
    receipt.orderNumber,
    receipt.fiskalReceiptNumber,
    receipt.fiskalStornoBy,
    ...(receipt.items ?? []).map((line) => line.productName),
  ]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(needle))
}

/**
 * Whether a receipt belongs under the chosen filter — FB-195.
 *
 * **Stornované means both storno states.** `receiptState` distinguishes a device storno from one marked
 * only in the books (`storned-virtual`), and the badge on the row keeps saying which — that distinction
 * is right and stays. But the *filter* matched the exact state, so on data where every storno is a local
 * one, the banner said *Stornované doklady sa počítajú, ale nie sú v obrate: 4* and selecting
 * **Stornované** showed nothing. The banner counts `fiskalStornoAt`, and so does this.
 */
function matchesState(receipt: FiskalReceipt, state: string): boolean {
  const actual = receiptState(receipt)
  if (state === 'storned') return actual === 'storned' || actual === 'storned-virtual'
  return actual === state
}

export function filterReceipts(
  receipts: FiskalReceipt[],
  { state, search }: { state: string; search: string },
): FiskalReceipt[] {
  return receipts.filter((receipt) => {
    if (state !== 'all' && !matchesState(receipt, state)) return false
    return matchesSearch(receipt, search)
  })
}

/**
 * What the detail panel shows as the breakdown.
 *
 * Reconciled against the server's own figures rather than recomputed: the receipt
 * carries `vatBase` and `vatAmount`, and this is a tax document (FB-064).
 */
export interface ReceiptBreakdown {
  subtotalCents: number
  discountCents: number
  deliveryCents: number
  vatBaseCents: number
  vatCents: number
  totalCents: number
  /** True when base + VAT does not equal the total the receipt claims. */
  inconsistent: boolean
}

export function receiptBreakdown(receipt: FiskalReceipt): ReceiptBreakdown {
  const totalCents = parseMoneyToCents(receipt.total)
  const vatBaseCents = parseMoneyToCents(receipt.vatBase)
  const vatCents = parseMoneyToCents(receipt.vatAmount)

  return {
    subtotalCents: parseMoneyToCents(receipt.subtotal),
    discountCents: parseMoneyToCents(receipt.discount),
    deliveryCents: parseMoneyToCents(receipt.deliveryCost),
    vatBaseCents,
    vatCents,
    totalCents,
    // Surfaced rather than hidden: a receipt whose tax lines do not add up to its
    // own total is a document somebody has to look at, and rounding it away is how
    // it stays unnoticed.
    inconsistent: vatBaseCents + vatCents !== totalCents,
  }
}
